import React from 'react';
import '@/App.css';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from '@/lib/auth';
import { Toaster } from '@/components/ui/sonner';
import { RequireAuth } from '@/components/common/RequireAuth';
import { LoadingScreen } from '@/components/common/LoadingScreen';
import { ErrorBoundary } from '@/components/common/ErrorBoundary';

import LoginPage from '@/pages/LoginPage';
import DashboardPage from '@/pages/DashboardPage';
import CustomersPage from '@/pages/CustomersPage';
import CustomerDetailPage from '@/pages/CustomerDetailPage';
import PropertiesPage from '@/pages/PropertiesPage';
import PropertyDetailPage from '@/pages/PropertyDetailPage';
import JobsPage from '@/pages/JobsPage';
import JobDetailPage from '@/pages/JobDetailPage';
import CalendarPage from '@/pages/CalendarPage';
import PipelinePage from '@/pages/PipelinePage';
import EstimatesPage from '@/pages/EstimatesPage';
import InvoicesPage from '@/pages/InvoicesPage';
import PaymentsPage from '@/pages/PaymentsPage';
import PriceBookPage from '@/pages/PriceBookPage';
import InventoryPage from '@/pages/InventoryPage';
import IssuesPage from '@/pages/IssuesPage';
import InboxPage from '@/pages/InboxPage';
import ReportsPage from '@/pages/ReportsPage';
import SettingsPage from '@/pages/SettingsPage';
import MyDayPage from '@/pages/MyDayPage';
import ProfilePage from '@/pages/ProfilePage';
import ServiceRequestsPage from '@/pages/ServiceRequestsPage';
import PrivacyPage from '@/pages/PrivacyPage';
import TermsPage from '@/pages/TermsPage';

// Portal
import PortalLoginPage from '@/pages/portal/PortalLoginPage';
import PortalDashboardPage from '@/pages/portal/PortalDashboardPage';
import PortalInvoicesPage from '@/pages/portal/PortalInvoicesPage';
import PortalInvoiceDetailPage from '@/pages/portal/PortalInvoiceDetailPage';
import PortalSchedulePage from '@/pages/portal/PortalSchedulePage';
import PortalMessagesPage from '@/pages/portal/PortalMessagesPage';
import PortalServiceRequestsPage from '@/pages/portal/PortalServiceRequestsPage';
import PortalReportsPage from '@/pages/portal/PortalReportsPage';
import PortalPropertiesPage from '@/pages/portal/PortalPropertiesPage';

// Redirect based on role — customers go to /portal.
function RootRedirect() {
  const { user, loading } = useAuth();
  if (loading) return <LoadingScreen />;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role === 'customer') return <Navigate to="/portal" replace />;
  return <DashboardPage />;
}

function App() {
  return (
    <AuthProvider>
      <ErrorBoundary>
        <BrowserRouter>
          <Routes>
            {/* Public */}
            <Route path="/login" element={<LoginPage />} />
            <Route path="/portal/login" element={<PortalLoginPage />} />
            <Route path="/portal/verify" element={<PortalLoginPage />} />
            <Route path="/privacy" element={<PrivacyPage />} />
            <Route path="/terms" element={<TermsPage />} />

            {/* Root — role-aware redirect */}
            <Route path="/" element={<RootRedirect />} />

            {/* Admin / Cleaner */}
            <Route path="/my-day" element={<RequireAuth roles={['admin', 'cleaner']}><MyDayPage /></RequireAuth>} />
            <Route path="/profile" element={<RequireAuth roles={['admin', 'cleaner']}><ProfilePage /></RequireAuth>} />
            <Route path="/customers" element={<RequireAuth roles={['admin']}><CustomersPage /></RequireAuth>} />
            <Route path="/customers/:id" element={<RequireAuth roles={['admin']}><CustomerDetailPage /></RequireAuth>} />
            <Route path="/properties" element={<RequireAuth roles={['admin']}><PropertiesPage /></RequireAuth>} />
            <Route path="/properties/:id" element={<RequireAuth roles={['admin']}><PropertyDetailPage /></RequireAuth>} />
            <Route path="/jobs" element={<RequireAuth roles={['admin', 'cleaner']}><JobsPage /></RequireAuth>} />
            <Route path="/jobs/:id" element={<RequireAuth roles={['admin', 'cleaner']}><JobDetailPage /></RequireAuth>} />
            <Route path="/calendar" element={<RequireAuth roles={['admin']}><CalendarPage /></RequireAuth>} />
            <Route path="/pipeline" element={<RequireAuth roles={['admin']}><PipelinePage /></RequireAuth>} />
            <Route path="/estimates" element={<RequireAuth roles={['admin']}><EstimatesPage /></RequireAuth>} />
            <Route path="/invoices" element={<RequireAuth roles={['admin']}><InvoicesPage /></RequireAuth>} />
            <Route path="/payments" element={<RequireAuth roles={['admin']}><PaymentsPage /></RequireAuth>} />
            <Route path="/pricebook" element={<RequireAuth roles={['admin']}><PriceBookPage /></RequireAuth>} />
            <Route path="/inventory" element={<RequireAuth roles={['admin', 'cleaner']}><InventoryPage /></RequireAuth>} />
            <Route path="/issues" element={<RequireAuth roles={['admin', 'cleaner']}><IssuesPage /></RequireAuth>} />
            <Route path="/service-requests" element={<RequireAuth roles={['admin']}><ServiceRequestsPage /></RequireAuth>} />
            <Route path="/inbox" element={<RequireAuth roles={['admin', 'cleaner']}><InboxPage /></RequireAuth>} />
            <Route path="/reports" element={<RequireAuth roles={['admin']}><ReportsPage /></RequireAuth>} />
            <Route path="/settings" element={<RequireAuth roles={['admin']}><SettingsPage /></RequireAuth>} />

            {/* Customer Portal */}
            <Route path="/portal" element={<RequireAuth roles={['customer']}><PortalDashboardPage /></RequireAuth>} />
            <Route path="/portal/invoices" element={<RequireAuth roles={['customer']}><PortalInvoicesPage /></RequireAuth>} />
            <Route path="/portal/invoices/:id" element={<RequireAuth roles={['customer']}><PortalInvoiceDetailPage /></RequireAuth>} />
            <Route path="/portal/schedule" element={<RequireAuth roles={['customer']}><PortalSchedulePage /></RequireAuth>} />
            <Route path="/portal/messages" element={<RequireAuth roles={['customer']}><PortalMessagesPage /></RequireAuth>} />
            <Route path="/portal/service-requests" element={<RequireAuth roles={['customer']}><PortalServiceRequestsPage /></RequireAuth>} />
            <Route path="/portal/reports" element={<RequireAuth roles={['customer']}><PortalReportsPage /></RequireAuth>} />
            <Route path="/portal/properties" element={<RequireAuth roles={['customer']}><PortalPropertiesPage /></RequireAuth>} />

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
      </ErrorBoundary>
      <Toaster position="top-right" richColors />
    </AuthProvider>
  );
}

export default App;
